import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Mic } from 'lucide-react';

import { QuestionLayout } from '../layout/QuestionLayout';
import { getTestId } from '../../utils/testUtils';
import { QuestionFlowProps, AnsweredQuestion, FlowCompletionContext } from './types';
import { QuestionFlowLoadingState } from './QuestionFlowLoadingState';

import { QuestionFlowEmptyState } from './QuestionFlowEmptyState';
import { ResponsePayload, QuestionFlowItemSummary, Question } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useDebug } from '../../contexts/DebugContext';
import { useQuestionFlowManager, type SubmitQuestionOptions } from './useQuestionFlowManager';
import { InvalidFlow } from './Invalidflow';
import { AccessDenied } from './AccessDenied';
import { useImagePreloader } from 'hooks/useImagePreloader';
import { isQuestionResponseValid } from 'utils/questionResponseValidation';
import { normalizeQuestionType } from 'utils/questionTypeUtils';
import { normalizeResponsePayload } from './responseUtils';
import { getErrorMessage } from './questionSessionHelpers';
import Popup from '../popup';
import { useFlowPrerequisiteGuard } from './useFlowPrerequisiteGuard';
import { QuestionFlowStage } from './QuestionFlowStage';
import { useQuestionFlowAnalytics } from './useQuestionFlowAnalytics';
import { courses, flows, responses } from '../../services/api';
import { FlowCompanion } from './FlowCompanion';
import { QuestionFlowControlsTour } from './QuestionFlowControlsTour';
import RoleplayFlowStep from 'pages/RoleplayPractice/RoleplayFlowStep';
import { FlowThemeLayer, ThemeContextProvider, type FlowThemeProgress } from './themes';
import { useLearnerThemeContext } from './themes/useLearnerThemeContext';
import { clearLifePlanLiveAnswer, setLifePlanLiveAnswer, themeAnswerFromResponse } from './themes/lifePlan/answers';
import { useFlowThemeTransitionReady } from './themes/FlowThemeTransition';
import { useGeneratedQuestion } from './useGeneratedQuestion';
import { hasAnswerReferences, useAnswerReferences } from './useAnswerReferences';
import { AnswerReferenceContext } from 'utils/answerReferences';
import { hasQuestionGeneration } from 'utils/questionGeneration';
import { isQuestionGenerationConflict } from 'services/api/questionGenerationClient';
import { QuestionGenerationRecovery } from './QuestionGenerationRecovery';

interface PendingSpeedWarning {
  response: ResponsePayload;
  options: SubmitQuestionOptions;
  shouldSubmitFlow: boolean;
  targetQuestionIndex: number;
  timeSpentMs: number;
  settlement?: { resolve: () => void; reject: (reason: unknown) => void };
}

const draftAnswerKey = (draft: ResponsePayload | null, questionType: string) => {
  const answer = normalizeResponsePayload(draft ?? undefined, questionType);
  return JSON.stringify([answer.rating, answer.text, answer.bool, answer.selected, answer.revealed,
    answer.custom, answer.audio, answer.textList]);
};

const QuestionFlow: React.FC<QuestionFlowProps> = ({
  flowName,
  courseId = null,
  targetDate,
  flowRunId,
  initialFlow = null,
  initialBootstrap = null,
  voiceEntryNotice = null,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEnabled: isDebugEnabled } = useDebug();
  const firstQuestionIdRef = useRef<number | null>(null);
  const activeQuestionRef = useRef<Question | null>(null);
  const draftVersionsRef = useRef<Record<number, { draft: ResponsePayload | null; version: number }>>({});
  const explicitSavePendingRef = useRef(false);
  const [explicitSavePending, setExplicitSavePending] = useState(false);
  const [generationRecovery, setGenerationRecovery] = useState<{ questionId: number; draft: ResponsePayload | null; questionText?: string; reply?: string } | null>(null);
  const lastRenderedQuestionIndexRef = useRef<number | null>(null);
  const pendingTransitionDirectionRef = useRef<1 | -1 | null>(null);
  const [transitionDirection, setTransitionDirection] = useState<1 | -1>(1);
  const [pendingSpeedWarning, setPendingSpeedWarning] = useState<PendingSpeedWarning | null>(null);
  const [isSpeedWarningSubmitting, setIsSpeedWarningSubmitting] = useState(false);
  const [questionSaveError, setQuestionSaveError] = useState<{ questionId: number; message: string } | null>(null);
  const [hasSubmittedFlowInSession, setHasSubmittedFlowInSession] = useState(false);
  const [isResolvingCompletionRedirect, setIsResolvingCompletionRedirect] = useState(false);
  const [tutorialLaunchToken, setTutorialLaunchToken] = useState(0);
  const [isThemeReady, setIsThemeReady] = useState(false);
  const resolvedInitialFlow = initialBootstrap?.flow ?? initialFlow;
  const { guardLoading, blockingFlow, lockedCourseId, errorMessage: guardError } = useFlowPrerequisiteGuard(flowName, {
    bypassPrerequisiteCheck: Boolean(user?.is_staff),
    courseId,
    initialResult: initialBootstrap?.prerequisite ?? null,
  });

  const {
    loading,
    error,
    isAccessDenied,
    lockedCourseId: runtimeLockedCourseId,
    flow,
    questions,
    responsesByQuestion,
    persistedRevisions,
    hasUnsavedChanges,
    isSubmitting,
    submitQuestionResponse,
    submitFlow,
    submissionResult,
    currentQuestionIndex,
    currentQuestion,
    progress,
    goToPreviousQuestion,
    adoptServerResponses,
  } = useQuestionFlowManager({
    flowName,
    initialFlow: resolvedInitialFlow,
    initialBootstrap,
    user,
    targetDate: targetDate ?? undefined,
    initialFlowRunId: flowRunId ?? undefined,
    courseId,
  });
  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  // The Life Plan book writes the learner's draft onto its page as they type (only that theme gets answers).
  const lifePlanBookRef = useRef(false);
  lifePlanBookRef.current = flow?.theme === 'life_plan' && Boolean(flow?.theme_content?.lifePlan);
  useEffect(() => () => clearLifePlanLiveAnswer(), []);

  if (firstQuestionIdRef.current == null && questions.length) {
    const initialQuestionCandidate = questions[0];
    if (initialQuestionCandidate && typeof initialQuestionCandidate.id === 'number') {
      firstQuestionIdRef.current = initialQuestionCandidate.id;
    }
  }

  const resolvedFlowName = flow?.name ?? flowName;
  const resolvedFlowTitle = flow?.title || resolvedFlowName;
  const resolvedFlowRunId = progress?.flowRunId ?? null;

  const {
    markQuestionStart,
    getQuestionDuration,
    evaluateSpeedWarning,
    trackFlowStepView,
    trackFlowContinue,
    trackFlowSubmit,
    trackFlowPrevious,
  } = useQuestionFlowAnalytics({
    flow,
    flowName: resolvedFlowName,
    flowRunId: resolvedFlowRunId,
    currentQuestionIndex,
  });

  const flowItemsByQuestionId = useMemo<Record<number, QuestionFlowItemSummary>>(() => {
    const items = Array.isArray(flow?.items) ? flow!.items! : [];
    const map: Record<number, QuestionFlowItemSummary> = {};

    // Learner flow payloads provide item metadata on each runtime question.
    // Explicit items, when supplied by another entry point, take precedence.
    questions.forEach((question, index) => {
      if (typeof question.id !== 'number' || typeof question.flow_item_id !== 'number') {
        return;
      }
      map[question.id] = {
        id: question.flow_item_id,
        question: question.id,
        question_id: question.id,
        order: typeof question.order === 'number' ? question.order : index,
        is_required: question.is_required !== false,
        conditions: null,
      };
    });

    items.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }

      const candidate = item as Record<string, any>;
      const questionRef = candidate.question ?? candidate.question_id;
      let questionId: number | undefined;

      if (typeof candidate.question_id === 'number') {
        questionId = candidate.question_id;
      } else if (typeof questionRef === 'number') {
        questionId = questionRef;
      } else if (questionRef && typeof questionRef === 'object' && typeof questionRef.id === 'number') {
        questionId = questionRef.id;
      }

      if (typeof questionId !== 'number') {
        return;
      }

      map[questionId] = {
        id: typeof candidate.id === 'number' ? candidate.id : questionId,
        question: questionId,
        question_id: questionId,
        order: typeof candidate.order === 'number' ? candidate.order : 0,
        is_required: candidate.is_required !== false,
        conditions: candidate.conditions ?? null,
      };
    });

    return map;
  }, [flow, questions]);
  const questionsWithResponses: AnsweredQuestion[] = useMemo(
    () =>
      questions.map((question) => {
        const questionId = question.id;
        return {
          question,
          response: questionId ? responsesByQuestion[questionId] : undefined,
          flowItem: questionId ? flowItemsByQuestionId[questionId] : undefined,
        };
      }),
    [questions, responsesByQuestion, flowItemsByQuestionId],
  );

  const completionContext: FlowCompletionContext = useMemo(() => {
    const responsesByKey: Record<string, ResponsePayload | undefined> = {};

    questions.forEach((question) => {
      if (!question || typeof question.id !== 'number' || typeof question.key !== 'string') {
        return;
      }
      responsesByKey[question.key] = responsesByQuestion[question.id];
    });

    return {
      responsesByKey,
      continuation: submissionResult?.continuation,
      targetDate: progress?.targetDate ?? targetDate ?? null,
    };
  }, [questions, progress?.targetDate, responsesByQuestion, submissionResult?.continuation, targetDate]);

  const answeredCount = useMemo(
    () =>
      questionsWithResponses.filter(({ question, response }) => {
        const questionId = question.id;
        if (typeof questionId !== 'number' || !response || response.question !== questionId) {
          return false;
        }

        if (response.is_completed !== true) {
          return false;
        }

        return isQuestionResponseValid(question, { draft: response });
      }).length,
    [questionsWithResponses],
  );

  const remainingQuestions = useMemo(() => {
    if (!questions.length) {
      return [];
    }
    const nextIndex = Math.min(currentQuestionIndex + 1, questions.length);
    return questions.slice(nextIndex);
  }, [questions, currentQuestionIndex]);

  const upcomingQuestionIds = useMemo(
    () =>
      remainingQuestions
        .filter((question) => !hasQuestionGeneration(question))
        .map((question) => question.id)
        .filter((questionId): questionId is number => typeof questionId === 'number')
        .slice(0, 2),
    [remainingQuestions],
  );

  const themeProgress = useMemo<FlowThemeProgress | null>(() => {
    if (!flow || !questions.length) {
      return null;
    }
    // While the completion redirect is checked the last card stays up, so the
    // finale waits for the completion card too.
    const isFinished = currentQuestionIndex >= questions.length && !isResolvingCompletionRedirect;
    return {
      step: isFinished ? questions.length : Math.min(Math.max(currentQuestionIndex, 0), questions.length - 1),
      total: questions.length,
      complete: isFinished,
    };
  }, [currentQuestionIndex, flow, isResolvingCompletionRedirect, questions.length]);

  const handleQuestionDraftChange = useCallback((questionId: number, draft: ResponsePayload | null) => {
    const current = draftVersionsRef.current[questionId];
    if (current?.draft === draft) return;
    // Renderers re-emit an equivalent draft whenever the saved response object is replaced.
    // Only a change to the answer itself counts, or Continue fails with "answer changed" forever.
    const question = questionsRef.current.find((candidate) => candidate.id === questionId);
    if (lifePlanBookRef.current && question?.key) setLifePlanLiveAnswer(question.key, themeAnswerFromResponse(question, draft));
    const questionType = normalizeQuestionType(question?.question_type_display ?? question?.question_type ?? '');
    const sameAnswer = current !== undefined
      && draftAnswerKey(current.draft, questionType) === draftAnswerKey(draft, questionType);
    draftVersionsRef.current[questionId] = { draft, version: (current?.version ?? 0) + (sameAnswer ? 0 : 1) };
  }, []);

  const handleGenerationConflict = useCallback((draft: ResponsePayload | null, reply?: string) => {
    const questionId = draft?.question ?? activeQuestionRef.current?.id;
    if (questionId == null) return;
    setGenerationRecovery({ questionId, draft: draftVersionsRef.current[questionId]?.draft ?? draft,
      questionText: activeQuestionRef.current?.id === questionId ? activeQuestionRef.current.text : undefined, reply });
  }, []);

  const processQuestionSubmission = useCallback(
    ({ response, options, shouldSubmitFlow, targetQuestionIndex, timeSpentMs }: PendingSpeedWarning) => {
      const submissionOptions: SubmitQuestionOptions = { ...options };
      const personalizedQuestion = hasQuestionGeneration(questions[targetQuestionIndex]);
      // The next card is built from saved answers (personalized wording or REQ-106 references).
      const nextReadsAnswers = hasQuestionGeneration(questions[targetQuestionIndex + 1])
        || hasAnswerReferences(questions[targetQuestionIndex + 1]);
      const shouldLock = shouldSubmitFlow || (submissionOptions.advance !== false
        && (personalizedQuestion || nextReadsAnswers || submissionOptions.serverPersisted));
      const draftVersion = draftVersionsRef.current[response.question]?.version;
      if (shouldLock) {
        explicitSavePendingRef.current = true;
        setExplicitSavePending(true);
        if (!submissionOptions.serverPersisted) submissionOptions.isCurrentSubmission = () => draftVersionsRef.current[response.question]?.version === draftVersion;
      }
      const settle = <T,>(promise: Promise<T>) => promise.finally(() => {
        if (shouldLock) { explicitSavePendingRef.current = false; setExplicitSavePending(false); }
      });
      const handleFailure = (failure: unknown) => {
        if (isQuestionGenerationConflict(failure)) handleGenerationConflict(response);
        if (submissionOptions.serverPersisted || submissionOptions.propagateErrors) throw failure;
        if (shouldSubmitFlow || personalizedQuestion || nextReadsAnswers) {
          const code = (failure as { code?: string })?.code;
          setQuestionSaveError({ questionId: response.question, message: code === 'answer_changed'
            ? 'Your answer changed while saving. Continue again with your latest answer.'
            : code === 'earlier_answer_unsaved'
              ? 'An earlier answer could not be saved. Go back to it and try again.'
              : shouldSubmitFlow ? getErrorMessage(failure)
                : 'Your answer could not be saved. Please try again before continuing.' });
        }
      };

      if (shouldSubmitFlow) {
        pendingTransitionDirectionRef.current = 1;
        setTransitionDirection(1);
        submissionOptions.persistImmediately = true;
      }

      if (submissionOptions.advance !== false) {
        pendingTransitionDirectionRef.current = 1;
        setTransitionDirection(1);
      }

      const submissionPromise = shouldSubmitFlow
        ? submitFlow({
            finalResponse: response,
            serverPersisted: submissionOptions.serverPersisted,
            isCurrentSubmission: submissionOptions.isCurrentSubmission,
            preserveQuestionOnError: true,
          })
        : submitQuestionResponse(response, submissionOptions);

      if (shouldSubmitFlow) {
        // Hold the last card on screen until the completion redirect is
        // resolved, rather than flashing the completion card first.
        setIsResolvingCompletionRedirect(true);
        return settle(submissionPromise
          .catch((persistenceError) => {
            console.error('Error saving response before submission:', persistenceError);
            throw persistenceError;
          })
          .then(() => {
            if (submissionOptions.isCurrentSubmission && !submissionOptions.isCurrentSubmission()) {
              throw Object.assign(new Error('Your answer changed while saving.'), { code: 'answer_changed' });
            }
            setIsResolvingCompletionRedirect(true);
            setHasSubmittedFlowInSession(true);
            trackFlowSubmit({
              response,
              targetQuestionIndex,
              totalQuestions: questions.length,
              answeredQuestions: answeredCount,
              timeSpentMs,
              currentQuestionIndex,
            });
          })
          .catch((submissionError) => {
            console.error('Error submitting flow:', submissionError);
            setIsResolvingCompletionRedirect(false);
            handleFailure(submissionError);
          }));
      }

      return settle(submissionPromise.catch((persistenceError) => {
        console.error('Error saving response:', persistenceError);
        handleFailure(persistenceError);
      }));
    },
    [
      answeredCount,
      handleGenerationConflict,
      currentQuestionIndex,
      questions,
      setHasSubmittedFlowInSession,
      submitFlow,
      submitQuestionResponse,
      trackFlowSubmit,
      setIsResolvingCompletionRedirect,
    ],
  );

  useEffect(() => {
    setHasSubmittedFlowInSession(false);
    setIsResolvingCompletionRedirect(false);
  }, [flowName, flowRunId, targetDate]);

  const handleQuestionSubmit = useCallback(
    (response: ResponsePayload, options?: SubmitQuestionOptions) => {
      if (!response?.question || explicitSavePendingRef.current) {
        return;
      }
      if (options?.advance !== false) setQuestionSaveError(null);

      const questionIndex = questions.findIndex((question) => question.id === response.question);
      const defaultAdvance = options?.advance ?? true;
      const shouldSubmitFlow = questionIndex >= 0 && questionIndex === questions.length - 1 && defaultAdvance;
      const nextOptions: SubmitQuestionOptions = {
        ...options,
        advance: shouldSubmitFlow ? false : defaultAdvance,
      };

      const targetQuestionIndex = questionIndex >= 0 ? questionIndex : currentQuestionIndex;
      const questionAtIndex =
        targetQuestionIndex >= 0 && targetQuestionIndex < questions.length ? questions[targetQuestionIndex] : undefined;
      const timeSpentMs = getQuestionDuration();
      const isAdvancing = nextOptions.advance !== false;

      trackFlowContinue({
        questionId: response.question,
        targetQuestionIndex,
        currentQuestionIndex,
        questionText: activeQuestionRef.current?.id === response.question ? activeQuestionRef.current.text : questionAtIndex?.text ?? null,
        timeSpentMs,
      });

      const shouldShowSpeedWarning = evaluateSpeedWarning({
        isAdvancing,
        timeSpentMs,
        isStaff: Boolean(user?.is_staff),
      });

      if (shouldShowSpeedWarning) {
        const pendingSubmission: PendingSpeedWarning = {
          response,
          options: nextOptions,
          shouldSubmitFlow,
          targetQuestionIndex,
          timeSpentMs,
        };
        if (options?.serverPersisted) {
          // Keep the evaluated card waiting for the user's decision and route
          // progress failures back to its existing continuation recovery UI.
          return new Promise<void>((resolve, reject) => {
            setPendingSpeedWarning({ ...pendingSubmission, settlement: { resolve, reject } });
          });
        }
        setPendingSpeedWarning(pendingSubmission);
        return;
      }

      return processQuestionSubmission({
        response,
        options: nextOptions,
        shouldSubmitFlow,
        targetQuestionIndex,
        timeSpentMs,
      });
    },
    [
      currentQuestionIndex,
      evaluateSpeedWarning,
      getQuestionDuration,
      processQuestionSubmission,
      questions,
      trackFlowContinue,
      user?.is_staff,
    ],
  );

  const handleSpeedWarningClose = useCallback(() => {
    pendingSpeedWarning?.settlement?.resolve();
    setPendingSpeedWarning(null);
    setIsSpeedWarningSubmitting(false);
  }, [pendingSpeedWarning, setIsSpeedWarningSubmitting, setPendingSpeedWarning]);

  const handleSpeedWarningConfirm = useCallback(() => {
    if (!pendingSpeedWarning || isSpeedWarningSubmitting) {
      return;
    }

    setIsSpeedWarningSubmitting(true);
    const submissionPayload = pendingSpeedWarning;
    setPendingSpeedWarning(null);

    Promise.resolve(
      processQuestionSubmission(submissionPayload),
    ).then(() => submissionPayload.settlement?.resolve(), (error) => {
      if (submissionPayload.settlement) submissionPayload.settlement.reject(error);
      else console.error('Error continuing after speed warning:', error);
    }).finally(() => {
      setIsSpeedWarningSubmitting(false);
    });
  }, [
    isSpeedWarningSubmitting,
    pendingSpeedWarning,
    processQuestionSubmission,
    setIsSpeedWarningSubmitting,
    setPendingSpeedWarning,
  ]);

  const isAtFlowEnd = currentQuestionIndex >= questions.length;
  const canEnterVoiceMode = Boolean(
    flow?.voice_mode_enabled
      && flow?.voice_mode_available
      && !flow?.is_quiz
      && resolvedFlowRunId
      && !isAtFlowEnd,
  );

  useEffect(() => {
    if (!hasSubmittedFlowInSession || !isAtFlowEnd || !flow?.name || typeof courseId !== 'number') {
      setIsResolvingCompletionRedirect(false);
      return;
    }

    if (submissionResult) {
      const continuation = submissionResult.continuation;
      if (continuation.course_completed && continuation.course) {
        navigate({ pathname: '/course_complete', search: `?courseId=${continuation.course.id}` }, {
          replace: true,
          state: {
            courseId: continuation.course.id,
            courseName: continuation.course.name,
            completedFlowName: flow.name,
            completedAt: continuation.course_completed_at ?? undefined,
          },
        });
      } else {
        setIsResolvingCompletionRedirect(false);
      }
      return;
    }

    let isActive = true;
    setIsResolvingCompletionRedirect(true);

    const resolveCompletionRedirect = async () => {
      try {
        const completionResponse = await courses.getCourseCompletion(courseId);
        if (!isActive) {
          return;
        }

        if (completionResponse?.data?.is_completed) {
          navigate(
            {
              pathname: '/course_complete',
              search: `?courseId=${courseId}`,
            },
            {
              replace: true,
              state: {
                courseId,
                courseName: completionResponse?.data?.course?.name ?? undefined,
                completedFlowName: flow.name,
                completedAt: completionResponse?.data?.completed_at ?? new Date().toISOString(),
              },
            },
          );
          return;
        }
      } catch (completionRedirectError) {
        console.warn('Unable to resolve completion redirect for', flow.name, completionRedirectError);
      }

      if (isActive) {
        setIsResolvingCompletionRedirect(false);
      }
    };

    void resolveCompletionRedirect();

    return () => {
      isActive = false;
    };
  }, [courseId, flow?.name, hasSubmittedFlowInSession, isAtFlowEnd, navigate, submissionResult]);
  const activeQuestionIndex =
    questions.length > 0
      ? Math.min(currentQuestionIndex, Math.max(questions.length - 1, 0))
      : 0;
  // Keep the final active question (including its saved personalized wording)
  // mounted while course completion is checked, then animate to the completion
  // card. Passing the same question to generation retains its snapshot identity.
  const isHoldingForCompletion = isAtFlowEnd && isResolvingCompletionRedirect && questions.length > 0;
  const isShowingFlowEnd = isAtFlowEnd && !isHoldingForCompletion;
  const authoredQuestion = isHoldingForCompletion
    ? questions[questions.length - 1] ?? null
    : isAtFlowEnd ? null : currentQuestion ?? questions[activeQuestionIndex];
  const generatedQuestion = useGeneratedQuestion({
    question: authoredQuestion,
    flowName: resolvedFlowName,
    flowRunId: resolvedFlowRunId,
    flowItemId: authoredQuestion?.id ? flowItemsByQuestionId[authoredQuestion.id]?.id : undefined,
    enabled: !flow?.is_quiz && !loading && !guardLoading && !isShowingFlowEnd,
  });
  const answerReferenceState = useAnswerReferences({
    question: authoredQuestion,
    flowName: resolvedFlowName,
    flowItemId: authoredQuestion?.id ? flowItemsByQuestionId[authoredQuestion.id]?.id : undefined,
    source: resolvedFlowRunId ? { flowRunId: resolvedFlowRunId } : null,
    responses: responsesByQuestion,
    persistedRevisions,
    enabled: !flow?.is_quiz && !loading && !guardLoading && !isShowingFlowEnd,
  });
  const questionToRender = generatedQuestion.question;
  activeQuestionRef.current = generatedQuestion.ready ? questionToRender : null;
  const heldQuestion = isHoldingForCompletion ? questionToRender : null;
  const stageQuestion = questionToRender;
  const stageQuestionId = stageQuestion?.id;
  const stageQuestionResponse = stageQuestionId ? responsesByQuestion[stageQuestionId] : undefined;
  const stageQuestionFlowItem = stageQuestionId ? flowItemsByQuestionId[stageQuestionId] : undefined;
  // A roleplay item takes over the stage: the practice runtime owns its own
  // layout and advances the flow only after an evaluator-confirmed win.
  const isRoleplayStep = Boolean(
    stageQuestion
      && normalizeQuestionType(
        stageQuestion.question_type_display ?? stageQuestion.question_type ?? '',
      ) === 'roleplay',
  );
  // Embedded roleplay steps keep their normal background: the practice shell is
  // transparent with dark text, and live roleplay audio shouldn't compete with
  // an animating scene. The layer stays mounted (hidden and paused), so it
  // comes back afterwards without fading in from scratch or replaying its intro.
  // Kept at the same position in every render below so the scene survives
  // loading states without restarting.
  const dynamicThemeContext = useLearnerThemeContext({
    flow, question: stageQuestion, firstName: user?.first_name,
    position: themeProgress?.step ?? 0, total: questions.length, completed: progress.completedQuestionCount,
    complete: isShowingFlowEnd, status: progress.status, responses: responsesByQuestion,
    loading: guardLoading || loading || generatedQuestion.loading || answerReferenceState.loading, saving: isSubmitting || explicitSavePending || isHoldingForCompletion,
    error: Boolean(error || guardError || generatedQuestion.error), direction: transitionDirection,
    runKey: `${resolvedFlowName}:${resolvedFlowRunId ?? 'pending'}`,
  });
  const backToThemeCourse = useCallback(() => {
    if (courseId !== null) navigate(`/training?courseId=${courseId}#courses`);
  }, [courseId, navigate]);
  const themeLayer = (
    <FlowThemeLayer
      theme={flow?.theme}
      progress={themeProgress}
      themeContext={dynamicThemeContext}
      onBackToCourse={courseId !== null ? backToThemeCourse : undefined}
      hidden={isRoleplayStep}
      onReadyChange={setIsThemeReady}
    />
  );
  const layoutThemeProgress = isRoleplayStep ? null : themeProgress;
  const isLoadingOverBackdrop = Boolean(layoutThemeProgress);

  const preloadTargets = useMemo(() => {
    if (!questions.length) {
      return [] as Question[];
    }

    const targets: Question[] = [];
    const firstQuestion = questions[0];

    if (firstQuestion) {
      targets.push(firstQuestion);
    }

    if (questionToRender && (!firstQuestion || questionToRender.id !== firstQuestion.id)) {
      targets.push(questionToRender);
    }

    if (remainingQuestions.length) {
      for (const question of remainingQuestions.slice(0, 3)) {
        if (!targets.some((existing) => existing.id === question.id)) {
          targets.push(question);
        }
      }
    }

    return targets;
  }, [questions, questionToRender, remainingQuestions]);

  const { isInitialImageReady } = useImagePreloader(preloadTargets, {
    initialQuestionId: firstQuestionIdRef.current,
    initialImageReady: initialBootstrap?.initialImageReady ?? false,
  });

  const shouldWaitForInitialImage =
    Boolean(firstQuestionIdRef.current != null) && !isInitialImageReady;

  const questionIndex = questionToRender
    ? questions.findIndex((question) => question.id === questionToRender.id)
    : -1;
  const questionId = questionToRender?.id;
  const questionResponse = questionId ? responsesByQuestion[questionId] : undefined;
  const questionFlowItem = questionId ? flowItemsByQuestionId[questionId] : undefined;
  const currentFlowItemId = questionFlowItem?.id ?? questionToRender?.flow_item_id;
  const generationHistory = questionResponse?.metadata?.ai_generation as { snapshot_id?: number; question_text?: string } | undefined;
  const activeGenerationRecovery = generationRecovery?.questionId === questionId ? generationRecovery : null;
  const needsGenerationRecovery = generatedQuestion.restartRequired || Boolean(activeGenerationRecovery)
    || Boolean(generationHistory && !hasQuestionGeneration(questionToRender));
  const debugMetadata = isDebugEnabled
    ? {
        flowId: flow?.id ?? null,
        flowName: flow?.name ?? flowName,
        flowFrequency: flow?.frequency ?? null,
        flowRunId: progress.flowRunId ?? null,
        flowStatus: progress.status,
        flowCompletedAt: progress.completedAt ?? null,
        progressIndex: progress.currentIndex,
        totalQuestions: progress.totalQuestions,
        questionIndex,
        questionStep: questionIndex >= 0 ? questionIndex + 1 : null,
        questionId: questionId ?? null,
        questionKey: questionToRender?.key ?? null,
        questionTypeDisplay: questionToRender?.question_type_display ?? null,
        questionType: questionToRender
          ? normalizeQuestionType(
              questionToRender.question_type_display ?? questionToRender.question_type ?? '',
            )
          : null,
        flowItemId: questionFlowItem?.id ?? null,
        isSubmitting,
        hasUnsavedChanges,
        draft: questionResponse ?? null,
      }
    : null;
  const isLastQuestion = questionIndex === questions.length - 1;
  const canGoToPrevious = currentQuestionIndex > 0;
  const shouldShowQuestionFlowControlsTour = Boolean(
    flow && !flow.is_quiz && !isAtFlowEnd && questionToRender && !isRoleplayStep && generatedQuestion.ready && !needsGenerationRecovery,
  );

  useEffect(() => {
    markQuestionStart(questionToRender?.id ?? null);
  }, [markQuestionStart, questionToRender?.id]);

  useEffect(() => {
    if (!questionToRender || questionIndex < 0 || !generatedQuestion.ready) {
      return;
    }

    const renderedQuestionId = questionToRender.id;
    if (typeof renderedQuestionId !== 'number') {
      return;
    }

    trackFlowStepView({
      questionId: renderedQuestionId,
      questionIndex,
      questionText: questionToRender.text ?? null,
    });
  }, [generatedQuestion.ready, questionIndex, questionToRender, trackFlowStepView]);

  useEffect(() => {
    if (isAtFlowEnd) {
      lastRenderedQuestionIndexRef.current = null;
      pendingTransitionDirectionRef.current = null;
      return;
    }

    if (questionIndex < 0) {
      return;
    }

    const previousIndex = lastRenderedQuestionIndexRef.current;

    if (previousIndex == null) {
      lastRenderedQuestionIndexRef.current = questionIndex;
      pendingTransitionDirectionRef.current = null;
      return;
    }

    if (questionIndex !== previousIndex) {
      if (pendingTransitionDirectionRef.current == null) {
        setTransitionDirection(questionIndex > previousIndex ? 1 : -1);
      }
    }

    lastRenderedQuestionIndexRef.current = questionIndex;
    pendingTransitionDirectionRef.current = null;
  }, [isAtFlowEnd, questionIndex]);

  const handlePreviousClick = useCallback(() => {
    if (explicitSavePendingRef.current) return;
    if (canGoToPrevious) {
      const timeSpentMs = getQuestionDuration();
      trackFlowPrevious({
        questionId: questionToRender?.id ?? null,
        questionIndex: questionIndex >= 0 ? questionIndex : currentQuestionIndex,
        currentQuestionIndex,
        questionText: questionToRender?.text ?? null,
        timeSpentMs,
      });
    }

    pendingTransitionDirectionRef.current = -1;
    setTransitionDirection(-1);
    goToPreviousQuestion();
  }, [
    canGoToPrevious,
    currentQuestionIndex,
    getQuestionDuration,
    goToPreviousQuestion,
    questionIndex,
    questionToRender,
    trackFlowPrevious,
  ]);

  const handleNewAttemptStarted = (runId: number) => {
    const params = new URLSearchParams();
    params.set('flowRunId', String(runId));
    params.set('mode', 'standard');
    const date = progress?.targetDate ?? targetDate;
    if (date) params.set('targetDate', date);
    navigate({ pathname: courseId != null ? `/courses/${courseId}/flows/${resolvedFlowName}` : `/flows/${resolvedFlowName}`, search: `?${params.toString()}` }, { replace: true });
  };

  // A won roleplay is recorded server-side. Adopt that response first so the
  // normal submit path advances (or completes the flow) without creating a
  // duplicate row, then reuse the standard continue handling.
  const handleRoleplayContinue = useCallback(async () => {
    const roleplayQuestionId = questionToRender?.id;
    if (typeof roleplayQuestionId !== 'number') {
      return;
    }

    if (typeof resolvedFlowRunId === 'number') {
      try {
        const listResponse = await responses.list({ flow_run: resolvedFlowRunId });
        const rows: Array<{ question?: number }> = Array.isArray(listResponse?.data)
          ? listResponse.data
          : [];
        adoptServerResponses(rows.filter((row) => row?.question === roleplayQuestionId));
      } catch (adoptError) {
        console.warn('Unable to load the recorded roleplay response', adoptError);
      }
    }

    const targetQuestionIndex = questionIndex >= 0 ? questionIndex : currentQuestionIndex;
    const shouldSubmitFlow = targetQuestionIndex === questions.length - 1;
    const timeSpentMs = getQuestionDuration();
    trackFlowContinue({
      questionId: roleplayQuestionId,
      targetQuestionIndex,
      currentQuestionIndex,
      questionText: questionToRender?.text ?? null,
      timeSpentMs,
    });

    await processQuestionSubmission({
      response: { question: roleplayQuestionId, is_completed: true },
      options: { advance: !shouldSubmitFlow, persistImmediately: true },
      shouldSubmitFlow,
      targetQuestionIndex,
      timeSpentMs,
    });
  }, [
    adoptServerResponses,
    currentQuestionIndex,
    getQuestionDuration,
    processQuestionSubmission,
    questionIndex,
    questionToRender,
    questions.length,
    resolvedFlowRunId,
    trackFlowContinue,
  ]);

  const handleOpenTutorial = useCallback(() => {
    setTutorialLaunchToken((currentValue) => currentValue + 1);
  }, []);

  const handleEnterVoiceMode = useCallback(async () => {
    const params = new URLSearchParams();
    params.set('mode', 'voice');

    const resolvedTargetDate = progress?.targetDate ?? targetDate;
    if (resolvedTargetDate) {
      params.set('targetDate', resolvedTargetDate);
    }
    if (resolvedFlowRunId) {
      params.set('flowRunId', String(resolvedFlowRunId));
    }

    if (typeof resolvedFlowRunId === 'number') {
      try {
        await flows.updateInteractionMode(resolvedFlowName, {
          flowRunId: resolvedFlowRunId,
          mode: 'voice',
        });
      } catch (modeError) {
        console.warn('Unable to record voice mode for flow run', modeError);
      }
    }

    navigate({
      pathname: typeof courseId === 'number'
        ? `/courses/${courseId}/flows/${resolvedFlowName}`
        : `/flows/${resolvedFlowName}`,
      search: `?${params.toString()}`,
    });
  }, [courseId, navigate, progress?.targetDate, resolvedFlowName, resolvedFlowRunId, targetDate]);

  const entryLoading = guardLoading || loading || shouldWaitForInitialImage;
  const entryUnavailable = Boolean(runtimeLockedCourseId || lockedCourseId || guardError
    || blockingFlow || isAccessDenied || error || !questions.length);
  const openingQuestionReady = Boolean(questionToRender)
    && !answerReferenceState.loading
    && (generatedQuestion.ready || Boolean(generatedQuestion.error) || Boolean(generationRecovery));
  useFlowThemeTransitionReady(
    !entryLoading && (entryUnavailable || (!isRoleplayStep && (openingQuestionReady || isAtFlowEnd))),
    { unthemed: entryUnavailable },
  );

  if (entryLoading) {
    return (
      <ThemeContextProvider value={dynamicThemeContext}>
        {themeLayer}
        <QuestionFlowLoadingState frosted={isLoadingOverBackdrop} />
      </ThemeContextProvider>
    );
  }

  if (runtimeLockedCourseId || lockedCourseId) {
    return <Navigate to={`/training?courseId=${runtimeLockedCourseId || lockedCourseId}#courses`} replace state={{ courseLocked: true }} />;
  }

  if (guardError) {
    return <AccessDenied message={guardError} courseId={courseId} onRetry={() => window.location.reload()} />;
  }

  if (blockingFlow) {
    return <InvalidFlow flow={blockingFlow} courseId={courseId} />;
  }

  if (isAccessDenied || error) {
    return <AccessDenied />;
  }

  if (!questions.length) {
    return <QuestionFlowEmptyState flowName={flowName} />;
  }

  if (!questionToRender && !isAtFlowEnd) {
    return (
      <ThemeContextProvider value={dynamicThemeContext}>
        {themeLayer}
        <QuestionFlowLoadingState frosted={isLoadingOverBackdrop} />
      </ThemeContextProvider>
    );
  }

  return (
    <ThemeContextProvider value={dynamicThemeContext}>
      {themeLayer}
      <QuestionLayout
      {...getTestId(isShowingFlowEnd ? `flow-${flowName}-completed` : `flow-${flowName}`)}
      progress={progress.progressPercent}
      flowName={resolvedFlowTitle}
      flowIdentifier={resolvedFlowName}
      courseId={courseId}
      courseContentKey={flow?.theme_content?.course?.key}
      onOpenTutorial={shouldShowQuestionFlowControlsTour ? handleOpenTutorial : undefined}
      theme={flow?.theme}
      themeProgress={layoutThemeProgress}
      themeReady={isThemeReady}
      headerActions={canEnterVoiceMode ? (
          <button
            type="button"
            className="inline-flex min-h-11 min-w-11 items-center justify-center gap-[0.4rem] rounded-full border border-white/60 bg-white/60 py-2 pl-3 pr-2.5 text-[0.75rem] font-semibold leading-4 text-foundation-base-darkbase-dark-500 shadow-[0_0.75rem_2rem_rgba(49,51,64,0.10),inset_0_1px_0_rgba(255,255,255,0.62)] backdrop-blur-xl transition hover:bg-white/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgba(253,67,25,0.38)] sm:gap-[0.45rem] sm:py-[0.8rem] sm:pl-[1.125rem] sm:pr-[0.95rem] sm:text-[0.8125rem]"
            onClick={() => { void handleEnterVoiceMode(); }}
            data-testid="question-flow-voice-mode-button"
            aria-label="Switch to voice mode"
          >
            <Mic className="h-4 w-4" />
            <span className="hidden sm:inline">Voice mode</span>
          </button>
      ) : undefined}
    >
      {voiceEntryNotice ? (
        <div className="mx-auto mt-4 w-full max-w-5xl px-4 sm:px-6 md:px-10">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {voiceEntryNotice}
          </div>
        </div>
      ) : null}
      {isRoleplayStep && stageQuestion && typeof stageQuestion.id === 'number' ? (
        typeof resolvedFlowRunId === 'number' ? (
          <RoleplayFlowStep
            key={stageQuestion.id}
            flowName={resolvedFlowName}
            flowRunId={resolvedFlowRunId}
            questionId={stageQuestion.id}
            canContinue={stageQuestionResponse?.is_completed === true}
            onContinue={handleRoleplayContinue}
            onBack={canGoToPrevious && !heldQuestion ? handlePreviousClick : undefined}
          />
        ) : (
          <QuestionFlowLoadingState />
        )
      ) : (
        <AnswerReferenceContext.Provider value={answerReferenceState.context}>
        <QuestionFlowStage
          onServerResponses={adoptServerResponses}
          isAtFlowEnd={isShowingFlowEnd}
          transitionDirection={transitionDirection}
          question={stageQuestion}
          generationId={generatedQuestion.generationId}
          questionLoading={generatedQuestion.loading || answerReferenceState.loading}
          questionError={generatedQuestion.error}
          submissionError={questionSaveError?.questionId === questionId ? questionSaveError?.message : undefined}
          onRetryQuestion={generatedQuestion.retry}
          onQuestionDraftChange={handleQuestionDraftChange}
          onGenerationConflict={handleGenerationConflict}
          recoveryContent={needsGenerationRecovery && resolvedFlowRunId && currentFlowItemId ? <QuestionGenerationRecovery
            flowName={resolvedFlowName} flowRunId={resolvedFlowRunId} flowItemId={currentFlowItemId}
            generationId={generatedQuestion.generationId ?? generationHistory?.snapshot_id}
            questionText={activeGenerationRecovery?.questionText ?? generationHistory?.question_text}
            draft={activeGenerationRecovery?.draft ?? questionResponse} reply={activeGenerationRecovery?.reply}
            options={questionToRender?.options}
            onStarted={handleNewAttemptStarted}
          /> : undefined}
          questionResponse={stageQuestionResponse}
          questionFlowItem={stageQuestionFlowItem}
          isSubmitting={isSubmitting || isHoldingForCompletion}
          isSavingResponse={explicitSavePending || isHoldingForCompletion}
          isLastQuestion={heldQuestion ? true : isLastQuestion}
          canGoToPrevious={canGoToPrevious && !heldQuestion}
          onPrevious={handlePreviousClick}
          onQuestionSubmit={handleQuestionSubmit}
          flow={flow}
          courseId={courseId}
          flowRunId={resolvedFlowRunId}
          currentQuestionIndex={heldQuestion ? questions.length - 1 : currentQuestionIndex}
          questionIndex={heldQuestion ? questions.length - 1 : questionIndex}
          upcomingQuestionIds={upcomingQuestionIds}
          debugMetadata={debugMetadata}
          completionContext={completionContext}
          bottomRightAccessory={
            flow && !flow.is_quiz && resolvedFlowRunId && stageQuestion && generatedQuestion.ready && !needsGenerationRecovery ? (
              <FlowCompanion
                flowName={resolvedFlowName}
                flowRunId={resolvedFlowRunId}
                currentQuestion={stageQuestion}
                currentResponse={stageQuestionResponse}
              />
            ) : null
          }
        />
        </AnswerReferenceContext.Provider>
      )}
      {shouldShowQuestionFlowControlsTour ? (
        <QuestionFlowControlsTour
          flowName={resolvedFlowName}
          isActive={shouldShowQuestionFlowControlsTour}
          launchToken={tutorialLaunchToken}
          questionId={questionToRender?.id ?? null}
        />
      ) : null}

      {pendingSpeedWarning && (
        <Popup
          onClose={handleSpeedWarningClose}
          title={"\"Slow and steady wins the race\""}
          description='Are you going to win?'
          header='HEY SPEED RACER'
          buttonText="Ok, slowing down to win!"
          onClickButton={handleSpeedWarningConfirm}
          isSubmitting={isSpeedWarningSubmitting}
          isButtonDisabled={isSpeedWarningSubmitting}
        />
      )}
    </QuestionLayout>
    </ThemeContextProvider>
  );
};

export default QuestionFlow;
